import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Card, CardBody, CardHeader } from 'reactstrap';

import { Button } from '~components';

import { bootstrapSession } from '~main/actions/session';

export const FIRST_RETRY_MS = 5000;
export const MAX_RETRY_MS = 60000;

/**
 * Shown when the page-load session check failed for a reason other than "no session" (offline, 5xx, the API's
 * auth-unavailable). The cookie may still be valid, so this retries with backoff instead of sending the user to
 * /login, and retries at once when the browser comes back online. A success or a definite "no session" changes
 * the session status, and PrivateRoute takes it from there.
 */
const SessionUnavailable: React.FC = () => {
  const dispatch = useAppDispatch();
  const [checking, setChecking] = useState(false);
  const delay = useRef(FIRST_RETRY_MS);
  const timer = useRef<number>();
  const mounted = useRef(true);

  const retry = async () => {
    window.clearTimeout(timer.current);
    setChecking(true);
    const result = await dispatch(bootstrapSession());
    if (!mounted.current) return;
    setChecking(false);
    if (bootstrapSession.rejected.match(result)) {
      delay.current = Math.min(delay.current * 2, MAX_RETRY_MS);
      timer.current = window.setTimeout(retry, delay.current);
    }
  };

  useEffect(() => {
    mounted.current = true;
    timer.current = window.setTimeout(retry, delay.current);
    window.addEventListener('online', retry);
    return () => {
      mounted.current = false;
      window.clearTimeout(timer.current);
      window.removeEventListener('online', retry);
    };
  }, []);

  return (
    <div className="d-flex justify-content-center mt-5 login">
      <Card className="rounded">
        <CardHeader className="d-flex justify-content-center bg-menu-background">
          <img src="/assets/primary_logo.png" alt="Logo" />
        </CardHeader>
        <CardBody className="d-flex flex-column text-center justify-content-center">
          <h3 data-cy="session-unavailable-header">We can&apos;t reach TurnPoint right now</h3>
          <p className="mt-2">You haven&apos;t been signed out. We&apos;ll keep trying in the background.</p>
          <Button
            onClick={retry}
            loading={checking}
            className="mx-auto mt-3 px-4"
            color="success"
            dataCy="session-unavailable-retry-button"
          >
            Try again
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};

export default SessionUnavailable;
